import {ProjectWorkflow} from "../masterdata/models/projectWorkflow";
import {Plate} from "../models-new/plate";
import {ProcessStep} from "../models-new/processStep";
import {ExtractionProtocolComponentName, ProtocolComponentName} from "../constants/ProtocolComponentMapping";
import {PlateStatus} from "../constants/Status";
import {Batch} from "../models-new/batch";
import {Protocol} from "../protocol-configuration/dto/Protocol";

const NOT_ELIGIBLE_PLATE_STATUES: string[] = [PlateStatus.NOT_PROCESSED, PlateStatus.REDO, PlateStatus.MERGED, PlateStatus.RETURNED];

export type Screen = "EXTRACTION" | "PLATE_PREPARATION" | "ARRAY_PREPARATION";

export function validateBatchEligibility(batch: Batch, upstreamProtocol: Protocol, downStreamProtocol: Protocol, screen: Screen) {
    if (screen === 'EXTRACTION') {
        return validateBatchAtExtraction(batch, upstreamProtocol);
    } else if (screen === "PLATE_PREPARATION") {
        return validateBatchAtPlateProcessing(batch, upstreamProtocol, downStreamProtocol);
    } else {
        return validateBatchAtArrayProcessing(batch, upstreamProtocol, downStreamProtocol);
    }
}

function validateCompleteStep(batch: Batch, validSteps: string[], context: string) {
    const lastStep = validSteps[validSteps.length - 1];
    const completionStep = batch.processSteps.find(processStep => processStep.step === lastStep);
    if (!completionStep?.endPUID || !completionStep?.endTimeStamp) {
        return {
            errorMessage: {
                severity: "error",
                summary: `Invalid batch status!`,
                detail: `Batch ${batch.batchID} has not completed ${context} yet! Current status: ${batch.status}`,
            },
            valid: false,
        };
    }
    return {valid: true}
}

function validateBatchAtExtraction(batch: Batch, protocol: Protocol, checkForCompletion = false) {
    if (batch.type !== 'EXTRACTION_BATCH') {
        return {
            errorMessage: {
                severity: 'error',
                summary: 'Invalid batch scanned!',
                detail: `Batch ${batch.batchID} does not require extraction processing`
            },
            valid: false
        };
    }
    if (!checkForCompletion) {
        return {valid: true};
    }
    const validExtractionSteps = protocol.protocolExtractionStepList.filter(step => step !== ExtractionProtocolComponentName.SAMPLE_PLACEMENT && step !== ExtractionProtocolComponentName.BATCHING);
    return validateCompleteStep(batch, validExtractionSteps, "extraction");
}

function validateBatchAtPlateProcessing(batch: Batch, upstreamProtocol: Protocol, downStreamProtocol: Protocol, checkForCompletion = false) {
    if (batch.type === "ARRAY_BATCH" || batch.type === "ANALYSIS_BATCH") {
        return {
            errorMessage: {
                severity: 'error',
                summary: 'Invalid batch scanned!',
                detail: `Batch ${batch.batchID} does not require plate processing`
            },
            valid: false
        };
    }
    if (batch.type === "EXTRACTION_BATCH") {
        //Batch made in extraction is being moved forward. Hence, validate extraction steps before proceeding
        const validationResult = validateBatchAtExtraction(batch, upstreamProtocol, true);
        if (!validationResult.valid) {
            return validationResult;
        }
    }
    if (!checkForCompletion) {
        return {valid: true};
    }
    const validPlateProcessingSteps = downStreamProtocol.protocolStepList.filter(step => step !== ProtocolComponentName.PRECELL);
    return validateCompleteStep(batch, validPlateProcessingSteps, "plate processing");
}

function validateBatchAtArrayProcessing(batch: Batch, upstreamProtocol: Protocol, downstreamProtocol: Protocol) {
    if (batch.type === 'EXTRACTION_BATCH' || batch.type === 'PLATE_BATCH') {
        //If batch type is either extraction or plate batch, invoking plate validation would internally invoke extraction protocol validation if needed.
        const validationResult = validateBatchAtPlateProcessing(batch, upstreamProtocol, downstreamProtocol, true);
        if (!validationResult.valid) {
            return validationResult;
        }
    }
    return {valid: true};
}

export function validateProtocolForPlate(
    plate: Plate,
    plateWorkflow: ProjectWorkflow,
    screen: string,
    protocolStepList: string[],
    protocolExtractionStepList: string[],
    protocolArrayStepList: string[],
) {
    if (plate.processSteps.length === 0) {
        return {errorMessage: generateErrorMessage(plate, 'Plate not received yet!!'), valid: false};
    }

    if (NOT_ELIGIBLE_PLATE_STATUES.includes(plate.status)) {
        return {errorMessage: generateErrorMessage(plate, 'Plate can not be used for processing!'), valid: false};
    }

    const plateStepsMap = createPlateStepsMap(plate);

    if (plate.type === "ARRAY" && screen === "ARRAY_PREPARATION") {
        return validateArrayPreparation(plate, plateWorkflow, protocolArrayStepList);
    }

    if (plate.type === 'PLATE' && checkForQCScan(plateWorkflow, plateStepsMap)) {
        return {
            errorMessage: generateErrorMessage(plate, `Plate status is : ${plate.status} and expected is QCSCAN_COMPLETED`),
            valid: false
        };
    }

    if (screen === "EXTRACTION") {
        return validateExtraction(plate, plateWorkflow);
    }

    return validateDownStream(plate, plateWorkflow, screen, protocolStepList, protocolExtractionStepList, plateStepsMap);
}

function validateArrayPreparation(plate: Plate, plateWorkflow: ProjectWorkflow, protocolArrayStepList: string[]) {
    if (plateWorkflow.downstream.length === 0) {
        return {errorMessage: generateErrorMessage(plate, 'Array does not need downstream processing'), valid: false};
    }
    if (protocolArrayStepList.length === 0) {
        return {errorMessage: generateErrorMessage(plate, 'Array does not have any process steps'), valid: false};
    }
    return {valid: true};
}

function checkForQCScan(plateWorkflow: ProjectWorkflow, plateStepsMap: Map<string, any>) {
    const qcScanStep = plateStepsMap.get("QC_SCAN");
    return checkForQCScanCompleted(plateWorkflow, qcScanStep, plateStepsMap);
}

function validateExtraction(plate: Plate, plateWorkflow: ProjectWorkflow) {
    if (plate.type === "ARRAY") {
        return {errorMessage: generateErrorMessage(plate, 'Array does not need upstream processing'), valid: false};
    }
    if (plateWorkflow.upstream.length === 0) {
        return {errorMessage: generateErrorMessage(plate, 'Plate does not need upstream processing'), valid: false};
    }
    return {valid: true};
}

function createPlateStepsMap(plate: Plate): Map<string, ProcessStep> {
    const plateStepsMap = new Map<string, ProcessStep>();
    plate.processSteps.forEach(processStep => {
        plateStepsMap.set(processStep.step, processStep);
    });
    return plateStepsMap;
}

export function getValidExtractionStep(protocolExtractionStepList: string[]): string {
    let protocolExtractionLastStep = protocolExtractionStepList[protocolExtractionStepList.length - 1];
    while (protocolExtractionLastStep === "SAMPLE_PLACEMENT" || protocolExtractionLastStep === "BATCHING") {
        protocolExtractionStepList.pop();
        protocolExtractionLastStep = protocolExtractionStepList[protocolExtractionStepList.length - 1];
    }
    return protocolExtractionLastStep;
}

function validateDownStream(plate: Plate, plateWorkflow: ProjectWorkflow, screen: string, protocolStepList: string[], protocolExtractionStepList: string[], plateStepsMap: Map<string, ProcessStep>) {

    if (plate.type === "ARRAY" && screen === "PLATE_PREPARATION") {
        return {errorMessage: generateErrorMessage(plate, "Array scanned on a wrong screen"), valid: false};
    }

    if (plateWorkflow.downstream.length === 0 || !(screen === "PLATE_PREPARATION" || screen === "ARRAY_PREPARATION")) {
        return {errorMessage: generateErrorMessage(plate, `Plate status is : ${plate.status}`), valid: false};
    }

    if (plateWorkflow.upstream.length === 0 && screen === "PLATE_PREPARATION") {
        if (plate.processSteps.length === 0) {
            return {errorMessage: generateErrorMessage(plate, 'Plate does not have any process steps'), valid: false};
        }
        return {valid: true};
    }

    if (plateWorkflow.upstream.length === 0 && screen === "ARRAY_PREPARATION") {
        if (protocolStepList.length === 0) {
            return {valid: true};
        }

    }

    const validationResult = isValidStep(plate, plateWorkflow, screen, protocolStepList, protocolExtractionStepList, plateStepsMap);

    if (validationResult.valid) {
        return {valid: true};
    }

    return {errorMessage: validationResult.errorMessage, valid: false};
}

function isValidStep(plate: Plate, plateWorkflow: ProjectWorkflow, screen: string, protocolStepList: string[], protocolExtractionStepList: string[], plateStepsMap: Map<string, ProcessStep>) {
    const protocolExtractionLastStep = getValidExtractionStep(protocolExtractionStepList);
    const extractionLastStep = plateStepsMap.get(protocolExtractionLastStep);
    const lastStepInPlateProcessing = plateStepsMap.get(protocolStepList[protocolStepList.length - 1]);

    if (plateWorkflow.upstream.length !== 0 && extractionLastStep?.endTimeStamp && (screen === "PLATE_PREPARATION" || (screen === "ARRAY_PREPARATION" && lastStepInPlateProcessing?.endTimeStamp))) {
        return {valid: true};

    }

    if (plateWorkflow.upstream.length === 0 && screen === "ARRAY_PREPARATION" && lastStepInPlateProcessing?.endTimeStamp) {
        return {valid: true};
    }

    return {errorMessage: generateErrorMessage(plate, `Plate status is : ${plate.status}`), valid: false};
}

function generateErrorMessage(plate: Plate, description: string) {
    return {
        severity: "error",
        summary: `Plate :${plate.plateID} not eligible`,
        detail: `${description}`
    };
}

function checkForQCScanCompleted(plateWorkflow: ProjectWorkflow, qcScanStep: ProcessStep, plateStepsMap: Map<string, ProcessStep>) {
    return plateWorkflow?.qcScan && (!qcScanStep?.endTimeStamp || plateStepsMap.size < 1);
}
